import React, { useContext } from 'react';
import classNames from 'classnames';
import { Box, BoxProps, TextButton } from '@wix/design-system';
import { ArrowLeft, ArrowRight } from '@wix/wix-ui-icons-common';
import { MainHeader, MainHeaderProps } from '../MainHeader';
import s from './DialogContainer.scss';
import { observer } from 'mobx-react';
import { AppContextProvider } from '../AppLoader';
import { ModalHeader } from '../ModalHeader';
import { useTranslation } from 'react-i18next';
import { dataHooks } from '../../dataHooks';
import { useIsRtl } from '../../hooks';

export interface DialogContainerProps extends BoxProps {
  mainHeaderProps?: MainHeaderProps; // default is empty object
  shouldUseCallbackInModalHeader?: boolean; // Modal header's back runs mainHeaderProps.backButtonCallback, as the main header does. Default: history back
  showMainHeader?: boolean; // default is true
  dataHook?: string;
  contentClasses?: any[];
  closeButtonDataHook?: string;
  autoCenter?: boolean; // When true, wraps output with centering. Default: isDialogCentered
}

export const DialogContainer: React.FC<DialogContainerProps> = observer(
  ({
    children,
    mainHeaderProps,
    shouldUseCallbackInModalHeader = false,
    showMainHeader = true,
    dataHook,
    className = [],
    contentClasses = [],
    closeButtonDataHook,
    ...props
  }) => {
    const {
      rootStore: { displayStore, onHeaderBackButtonClicked, navigationStore },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const isRtl = useIsRtl();
    const isLargeLogo =
      parseInt(displayStore.preset.logo?.height ?? '0', 10) > 48;

    // Determine if back button should show above dialog
    const showBackAboveDialog =
      displayStore.isBackButtonAboveDialog && // feature enabled
      navigationStore.backButtonVisibility !== 'hidden' && // not explicitly hidden
      (navigationStore.backButtonVisibility === 'visible' || // explicitly visible
        (mainHeaderProps?.showBackButton !== false && // prop allows it
          !displayStore.isMobile)); // and not on mobile

    return (
      <>
        {showMainHeader && displayStore.showMainHeader && (
          <MainHeader {...mainHeaderProps} />
        )}
        <Box
          direction="vertical"
          align="center"
          {...props}
          className={classNames(
            displayStore.isMobile ? s.mobileContainer : s.desktopContainer,
            {
              [s.largeLogo]: isLargeLogo,
              [s.vertical]: displayStore.isVerticalLayout,
            },
            s[displayStore.dialogContainerClass],
            className,
          )}
          dataHook={dataHook}
        >
          {displayStore.showModalHeader && (
            <ModalHeader
              showBackButton={mainHeaderProps?.showBackButton}
              backButtonCallback={
                shouldUseCallbackInModalHeader
                  ? () =>
                      onHeaderBackButtonClicked(
                        mainHeaderProps?.backButtonCallback,
                      )
                  : undefined
              }
              closeButtonDataHook={closeButtonDataHook}
            />
          )}
          {showBackAboveDialog && (
            <Box
              className={s.backButtonAboveDialog}
              dataHook={dataHooks.app.backBtnAboveDialog}
              width="100%"
              maxWidth="536px"
            >
              <TextButton
                dataHook={dataHooks.app.backBtn}
                prefixIcon={isRtl ? <ArrowRight /> : <ArrowLeft />}
                size="medium"
                weight="normal"
                skin="dark"
                underline="onHover"
                onClick={() =>
                  onHeaderBackButtonClicked(mainHeaderProps?.backButtonCallback)
                }
              >
                {t('dialogHeader.backButton.text')}
              </TextButton>
            </Box>
          )}
          <div className={classNames(...contentClasses, s.dialogEnter)}>
            {children}
          </div>
        </Box>
      </>
    );
  },
);

export const DialogFormContainer: React.FC<
  DialogContainerProps & { formClasses?: string[] }
> = ({ children, formClasses = [], autoCenter, ...props }) => {
  const {
    rootStore: {
      displayStore: {
        isVerticalLayout,
        isMobile,
        isMobileFullScreen,
        isDialogCentered,
      },
    },
  } = useContext(AppContextProvider);

  // Use autoCenter prop if provided, otherwise default to isDialogCentered
  const shouldAutoCenter = autoCenter ?? isDialogCentered;

  const content = (
    <DialogContainer
      {...props}
      contentClasses={[
        classNames(
          s.form,
          {
            [s.vertical]: isVerticalLayout,
            [s.mobile]: isMobile,
            [s.mobileFullScreen]: isMobileFullScreen,
          },
          ...formClasses,
        ),
      ]}
    >
      {children}
    </DialogContainer>
  );

  // Wrap with absolute centering for secondary dialogs (matches Figma positioning)
  if (shouldAutoCenter) {
    return (
      <Box direction="vertical" width="100%" className="dialogAbsoluteCentered">
        {content}
      </Box>
    );
  }

  return content;
};
